import { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { ColorNeutralBlack, Spacing12 } from 'autogen/design-tokens/tokens';
import AspectRatioContainer from 'components/generic/AspectRatioContainer/AspectRatioContainer';
import GenericImage from 'components/generic/Image';
import QuickView, { useQuickView } from 'components/quickView';
import VideoPlayer from 'components/video/VideoPlayer';
import dynamic from 'helpers/dynamic';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import type { TypeVideoDetailed } from 'types/autogen/contentful';
import { breakpoint } from 'variables';
import WhenCFPreview from '../../shared/WhenCFPreview';
import Image from '../Image/Image';
import RichText from '../shared/RichText';

// TODO: the YoutubePlaceholder component also includes some youtube specific options. How should we include those?

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing12};
`;

const CoverImageWrapper = styled.div`
    height: 100%;
    width: 100%;

    div {
        height: 100%;
    }

    & img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ImageContainer = styled.div`
    height: 100%;
    width: 100%;
`;

const StyledGenericImage = styled(GenericImage)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ImageOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const PlayButton = styled.button`
    background: none;
    border: none;
    &:focus {
        outline: 0;
    }
`;

const StyledPlayIcon = styled(dynamic(() => import('icons/Play.svg?react')))`
    width: 85px;
    height: 85px;
`;

const StyledQuickView = styled(QuickView)`
    background-color: ${ColorNeutralBlack};

    && {
        width: 100%;

        ${breakpoint.up('768px')} {
            min-width: 768px;
            width: 768px;
        }
    }
`;

function getVideoThumbnail(url: string) {
    const MATCH_URL_YOUTUBE =
        /(?:youtu\.be\/|youtube(?:-nocookie|education)?\.com\/(?:embed\/|v\/|watch\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))((\w|-){11})|youtube\.com\/playlist\?list=|youtube\.com\/user\//;

    if (MATCH_URL_YOUTUBE.test(url)) {
        const regExp = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
        const id = url.match(regExp)?.[1];
        return `https://img.youtube.com/vi/${id}/0.jpg`;
    }

    return undefined;
}

function RenderImage(fields: TypeVideoDetailed['fields']) {
    const { coverImage, url, alternativeText } = fields;

    if (coverImage)
        return (
            <CoverImageWrapper>
                <Image {...coverImage} />
            </CoverImageWrapper>
        );

    if (!url) return null;

    return <StyledGenericImage src={getVideoThumbnail(url) || ''} alt={alternativeText} />;
}

function Video({ fields, sys }: TypeVideoDetailed) {
    const { coverImage, autoPlay, viewPresentation, url, caption } = fields;
    const entryId = sys.id;
    const inspectorProps = useContentfulInspectorMode({ entryId });

    // TODO: Should we get a sources array from the server?

    const [started, setStarted] = useState(autoPlay);
    const { toggle } = useQuickView(entryId);
    const isModal = viewPresentation === 'Modal';
    const isFunctionalCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);

    function handlePlay() {
        if (isModal) {
            toggle(true);
        }

        setStarted(true);
    }

    const Player = (
        <AspectRatioContainer ratioHeight={16} ratioWidth={9}>
            <TrackingContext context="contentful">
                <VideoPlayer {...(isModal ? {} : { muted: autoPlay })} video={{ url: url }} image={coverImage} />
            </TrackingContext>
        </AspectRatioContainer>
    );

    const WrappedPlayer = isModal ? (
        <StyledQuickView
            userCanClose
            onClose={() => toggle(false)}
            fullscreenOnMobile
            quickViewId={entryId}
            headerRenderer={() => null}
        >
            {Player}
        </StyledQuickView>
    ) : (
        (started || !isFunctionalCookieAccepted) && Player
    );

    return (
        <Wrapper {...inspectorProps({ fieldId: 'url' })}>
            {WrappedPlayer}
            {(!isModal && started) || (!isFunctionalCookieAccepted && !isModal) ? null : (
                <AspectRatioContainer ratioHeight={16} ratioWidth={9}>
                    <ImageContainer>
                        <RenderImage {...fields} />
                        <ImageOverlay>
                            <PlayButton type="button" onClick={handlePlay}>
                                <StyledPlayIcon />
                            </PlayButton>
                        </ImageOverlay>
                    </ImageContainer>
                </AspectRatioContainer>
            )}

            {caption && (
                <WhenCFPreview id={entryId} fieldId="caption">
                    <RichText {...caption} />
                </WhenCFPreview>
            )}
        </Wrapper>
    );
}

export default withErrorBoundary(Video, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
